/**
 * Monitoring Service
 *
 * Centralized service for managing multiple monitoring providers
 * Provides unified API for error tracking, session replay, and analytics
 *
 * Benefits:
 * - Single interface for all monitoring tools
 * - Easy to add/remove providers without code changes
 * - Automatic provider initialization based on environment config
 * - Graceful degradation if providers fail to initialize
 */

import { env, isProduction } from '@/shared/lib/env';
import { logRocketProvider } from './providers/logrocket.provider';
import { sentryProvider } from './providers/sentry.provider';
import type {
  MonitoringProvider,
  UserTraits,
  ErrorContext,
  EventProperties,
  BreadcrumbData,
} from './types';

/**
 * Main monitoring service class
 * Manages multiple monitoring providers and provides unified API
 */
class MonitoringService {
  private providers: readonly MonitoringProvider[] = [];
  private initialized = false;

  /**
   * Initialize all enabled monitoring providers
   * Called once during app startup
   */
  initialize(): void {
    if (this.initialized) {
      console.warn('MonitoringService: Already initialized');
      return;
    }

    const activeProviders: MonitoringProvider[] = [];

    // Add LogRocket if either session replay OR error tracking is enabled
    if (
      env.VITE_LOGROCKET_APP_ID &&
      (env.VITE_ENABLE_SESSION_REPLAY || env.VITE_ENABLE_ERROR_TRACKING)
    ) {
      activeProviders.push(logRocketProvider);
    }

    // Add Sentry if error tracking OR performance monitoring is enabled
    if (
      env.VITE_SENTRY_DSN &&
      (env.VITE_ENABLE_ERROR_TRACKING || env.VITE_ENABLE_PERFORMANCE_MONITORING)
    ) {
      activeProviders.push(sentryProvider);
    }

    // Future providers can be added here:
    // if (env.VITE_ENABLE_ANALYTICS && env.VITE_MIXPANEL_TOKEN) {
    //   activeProviders.push(mixpanelProvider);
    // }

    this.providers = activeProviders;

    // Initialize all providers
    for (const provider of this.providers) {
      try {
        provider.initialize();
      } catch (error) {
        console.error(
          `MonitoringService: Failed to initialize ${provider.name}:`,
          error
        );
      }
    }

    this.initialized = true;
    if (!isProduction) {
      console.log(
        `MonitoringService initialized with ${this.providers.length} provider(s)`
      );
    }

    // Link LogRocket and Sentry for enhanced error debugging
    this.linkProviders();
  }

  /**
   * Link monitoring providers together
   * Connects LogRocket session URLs to Sentry errors for complete context
   */
  private linkProviders(): void {
    try {
      // Get LogRocket session URL and share with Sentry
      if (logRocketProvider && sentryProvider) {
        // LogRocket session URL is only available after a session starts
        // We'll update this in identifyUser and captureError methods
        // by calling logRocketProvider.getSessionURL() when available
      }
    } catch (error) {
      console.error('MonitoringService: Failed to link providers:', error);
    }
  }

  /**
   * Identify user across all monitoring tools
   * Call this after successful login
   *
   * @param userId - Unique user identifier
   * @param traits - Additional user information
   *
   * @example
   * monitoringService.identifyUser('user-123', {
   *   name: 'John Doe',
   *   email: 'john@example.com',
   *   role: 'ADMIN'
   * });
   */
  identifyUser(userId: string, traits?: UserTraits): void {
    for (const provider of this.providers) {
      try {
        provider.identifyUser(userId, traits);
      } catch (error) {
        console.error(
          `MonitoringService: Failed to identify user in ${provider.name}:`,
          error
        );
      }
    }

    // Link LogRocket session URL to Sentry after user is identified
    this.syncLogRocketToSentry();
  }

  /**
   * Capture error across all monitoring tools
   * Automatically called by error boundary and API client
   *
   * @param error - Error instance to capture
   * @param context - Additional context for debugging
   *
   * @example
   * monitoringService.captureError(new Error('Payment failed'), {
   *   tags: { feature: 'checkout', step: 'payment' },
   *   extra: { amount: 99.99, currency: 'USD' }
   * });
   */
  captureError(error: Error, context?: ErrorContext): void {
    // Sync LogRocket session to Sentry before capturing error
    this.syncLogRocketToSentry();

    for (const provider of this.providers) {
      try {
        provider.captureError(error, context);
      } catch (captureError) {
        console.error(
          `MonitoringService: Failed to capture error in ${provider.name}:`,
          captureError
        );
      }
    }
  }

  /**
   * Sync LogRocket session URL to Sentry
   * This allows click-through from Sentry errors to LogRocket replays
   */
  private syncLogRocketToSentry(): void {
    try {
      // Check if both providers are initialized
      const hasLogRocket = this.providers.includes(logRocketProvider);
      const hasSentry = this.providers.includes(sentryProvider);

      if (!hasLogRocket || !hasSentry) return;

      // Get LogRocket session URL from globalThis (properly typed in vite-env.d.ts)
      const sessionURL = globalThis.LogRocket?.sessionURL;

      if (sessionURL) {
        // Share session URL with Sentry
        sentryProvider.setLogRocketSession(sessionURL);
      }
    } catch {
      // Silently fail - linking is optional enhancement
      console.debug('MonitoringService: Could not sync LogRocket to Sentry');
    }
  }

  /**
   * Track custom event across all monitoring tools
   * Use for important user actions and business events
   *
   * @param event - Event name (use descriptive names)
   * @param properties - Event metadata
   *
   * @example
   * monitoringService.trackEvent('Report Generated', {
   *   reportType: 'monthly',
   *   format: 'pdf',
   *   duration: 2.5
   * });
   */
  trackEvent(event: string, properties?: EventProperties): void {
    for (const provider of this.providers) {
      try {
        provider.trackEvent(event, properties);
      } catch (error) {
        console.error(
          `MonitoringService: Failed to track event in ${provider.name}:`,
          error
        );
      }
    }
  }

  /**
   * Add breadcrumb for debugging context
   * Breadcrumbs help understand the sequence of events leading to an error
   *
   * @param message - Breadcrumb message
   * @param data - Additional breadcrumb data
   *
   * @example
   * monitoringService.addBreadcrumb('User clicked export button', {
   *   section: 'reports',
   *   format: 'csv'
   * });
   */
  addBreadcrumb(message: string, data?: BreadcrumbData): void {
    for (const provider of this.providers) {
      try {
        provider.addBreadcrumb(message, data);
      } catch (error) {
        console.error(
          `MonitoringService: Failed to add breadcrumb in ${provider.name}:`,
          error
        );
      }
    }
  }

  /**
   * Set custom data for current session
   * Useful for adding context that persists across events
   *
   * @param key - Data key
   * @param value - Data value
   *
   * @example
   * monitoringService.setCustomData('selectedTheme', 'dark');
   * monitoringService.setCustomData('subscriptionTier', 'premium');
   */
  setCustomData(key: string, value: unknown): void {
    for (const provider of this.providers) {
      try {
        provider.setCustomData(key, value);
      } catch (error) {
        console.error(
          `MonitoringService: Failed to set custom data in ${provider.name}:`,
          error
        );
      }
    }
  }

  /**
   * Clear user identification on logout
   * Call this when user logs out
   *
   * @example
   * monitoringService.clearUser();
   */
  clearUser(): void {
    for (const provider of this.providers) {
      try {
        provider.clearUser?.();
      } catch (error) {
        console.error(
          `MonitoringService: Failed to clear user in ${provider.name}:`,
          error
        );
      }
    }
  }

  /**
   * Get list of initialized providers (for debugging)
   */
  getProviders(): readonly string[] {
    return this.providers.map((p) => p.name);
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  // ── PWA-specific tracking ──

  /**
   * Track PWA-related events and metrics.
   */
  trackPwaEvent(
    event:
      | 'pwa.sw_update_prompted'
      | 'pwa.sw_update_accepted'
      | 'pwa.sw_update_dismissed'
      | 'pwa.install_prompted'
      | 'pwa.install_accepted'
      | 'pwa.install_dismissed'
      | 'pwa.offline_start'
      | 'pwa.offline_end'
      | 'pwa.offline_mutation_queued'
      | 'pwa.offline_mutations_synced',
    properties?: EventProperties
  ): void {
    this.trackEvent(event, properties);
  }
}

/**
 * Singleton instance
 * Import and use this throughout the application
 */
export const monitoringService = new MonitoringService();
